/**
 * Crons — the platform job switchboard.
 *
 * Every background job registers with the cron registry and journals every
 * execution in cron_runs (cluster truth, not APScheduler's per-worker
 * memory). This page lists the registry with last-run outcomes, flips jobs
 * on/off without a deploy, runs one on demand, and shows recent history.
 * Jobs are platform-level: one pass covers all organizations.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, CronJob, CronRun } from '../lib/api';
import {
  Clock, Play, Loader2, CheckCircle2, XCircle, RefreshCw, Power, History,
} from 'lucide-react';

const DAYS_SUNDAY_FIRST = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_MONDAY_FIRST = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_BY_NAME: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

const pad2 = (n: number) => String(n).padStart(2, '0');
const isNum = (f: string) => /^\d+$/.test(f);
const reason = (e: unknown) => (e instanceof Error ? e.message : String(e));

interface ScheduleFields {
  minute: string;
  hour: string;
  dayOfWeek: string;
  /** APScheduler numbers weekdays from Monday = 0; classic cron from Sunday = 0. */
  mondayFirst: boolean;
}

/**
 * The registry stores APScheduler CronTrigger kwargs ("minute='*\/5'",
 * "hour=16, minute=30"); classic 5-field cron is accepted as well.
 */
function parseSchedule(schedule: string): ScheduleFields | null {
  const s = schedule.trim();
  const cron = s.split(/\s+/);
  if (cron.length === 5 && !s.includes('=')) {
    const [minute, hour, dayOfMonth, month, dayOfWeek] = cron;
    if (dayOfMonth !== '*' || month !== '*') return null;
    return { minute, hour, dayOfWeek, mondayFirst: false };
  }
  const fields: Record<string, string> = {};
  const pair = /(\w+)\s*=\s*('[^']*'|"[^"]*"|[^,\s]+)/g;
  let match: RegExpExecArray | null;
  while ((match = pair.exec(s)) !== null) {
    fields[match[1]] = match[2].replace(/^['"]|['"]$/g, '');
  }
  if (!Object.keys(fields).length) return null;
  if (fields.day || fields.month || fields.week || fields.year || fields.second) return null;
  return {
    minute: fields.minute ?? '*',
    hour: fields.hour ?? '*',
    dayOfWeek: fields.day_of_week ?? '*',
    mondayFirst: true,
  };
}

function dayName(token: string, mondayFirst: boolean): string | null {
  const t = token.trim().toLowerCase();
  if (isNum(t)) {
    const i = Number(t);
    const name = mondayFirst ? DAYS_MONDAY_FIRST[i] : DAYS_SUNDAY_FIRST[i % 7];
    return name || null;
  }
  return DAY_BY_NAME[t.slice(0, 3)] || null;
}

function describeDays(dayOfWeek: string, mondayFirst: boolean): string | null {
  const range = /^([^,]+)-([^,]+)$/.exec(dayOfWeek);
  if (range) {
    const from = dayName(range[1], mondayFirst);
    const to = dayName(range[2], mondayFirst);
    return from && to ? `${from} to ${to}` : null;
  }
  const names = dayOfWeek.split(',').map(d => dayName(d, mondayFirst));
  return names.every(Boolean) ? names.join(', ') : null;
}

/** Plain reading of a schedule, or null when the shape is not one we recognise. */
function describeCron(schedule: string): string | null {
  const fields = parseSchedule(schedule);
  if (!fields) return null;
  const { minute, hour, dayOfWeek, mondayFirst } = fields;
  const stepOf = (f: string) => {
    const m = /^(?:\*|\d+-\d+)\/(\d+)$/.exec(f);
    return m ? Number(m[1]) : null;
  };
  const rangeStart = (f: string) => {
    const m = /^(\d+)-\d+\/\d+$/.exec(f);
    return m ? Number(m[1]) : null;
  };
  const clock = () => `${pad2(Number(hour))}:${pad2(Number(minute))} UTC`;

  if (dayOfWeek === '*') {
    if (hour === '*') {
      if (minute === '*') return 'Every minute';
      const step = stepOf(minute);
      if (step) {
        const base = step === 1 ? 'Every minute' : `Every ${step} minutes`;
        const start = rangeStart(minute);
        return start ? `${base}, from :${pad2(start)} past each hour` : base;
      }
      if (isNum(minute)) return `Every hour at :${pad2(Number(minute))}`;
      return null;
    }
    const hourStep = stepOf(hour);
    if (hourStep && isNum(minute)) {
      return hourStep === 1
        ? `Every hour at :${pad2(Number(minute))}`
        : `Every ${hourStep} hours at :${pad2(Number(minute))}`;
    }
    if (isNum(hour) && isNum(minute)) return `Daily at ${clock()}`;
    return null;
  }
  if (isNum(hour) && isNum(minute)) {
    const days = describeDays(dayOfWeek, mondayFirst);
    if (days) return `Weekly on ${days} at ${clock()}`;
  }
  return null;
}

function describeValue(value: unknown): string {
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (value === null || value === undefined) return '—';
  return JSON.stringify(value);
}

/** "processed 1,204 · skipped 3"; nested structures fall back to JSON. */
function describeResult(result: unknown): string | null {
  if (result === null || result === undefined) return null;
  if (typeof result !== 'object' || Array.isArray(result)) return JSON.stringify(result);
  const entries = Object.entries(result as Record<string, unknown>);
  if (!entries.length) return null;
  return entries
    .map(([key, value]) => `${key.replace(/_/g, ' ')} ${describeValue(value)}`)
    .join(' · ');
}

export function Crons() {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyJob, setBusyJob] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [runs, setRuns] = useState<CronRun[]>([]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const data = await api.listCrons();
      setJobs(data.jobs);
    } catch (e) {
      setLoadError(`Couldn't load background jobs. Retry, or check the API is reachable. (${reason(e)})`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function toggle(job: CronJob) {
    if (job.enabled && !window.confirm(`Turn off "${job.name}"? It stops running until you turn it back on.`)) return;
    setBusyJob(job.job_id);
    setActionError(null);
    try {
      const data = await api.toggleCron(job.job_id);
      setJobs(data.jobs);
    } catch (e) {
      setActionError(`Couldn't turn ${job.enabled ? 'off' : 'on'} ${job.name}: ${reason(e)}`);
    } finally {
      setBusyJob(null);
    }
  }

  async function runNow(job: CronJob) {
    if (!window.confirm(`Run "${job.name}" now? It processes every organization in one pass.`)) return;
    setBusyJob(job.job_id);
    setActionError(null);
    try {
      await api.runCron(job.job_id);
      await load();
      if (historyFor === job.job_id) await showHistory(job.job_id);
    } catch (e) {
      setActionError(`Couldn't run ${job.name}: ${reason(e)}`);
    } finally {
      setBusyJob(null);
    }
  }

  async function showHistory(jobId: string) {
    setHistoryFor(jobId);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await api.cronRuns(jobId);
      setRuns(data.runs);
    } catch (e) {
      setRuns([]);
      setHistoryError(`Couldn't load the run history: ${reason(e)}`);
    } finally {
      setHistoryLoading(false);
    }
  }

  const historyJob = jobs.find(j => j.job_id === historyFor);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-6 w-6" /> Crons
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Platform background jobs. One pass covers all organizations; each organization's data
            stays scoped inside the job. Toggle without a deploy, run on demand,
            and audit every execution.
          </p>
        </div>
        <button onClick={load} title="Refresh jobs" aria-label="Refresh jobs" className="p-2 rounded-lg border hover:bg-gray-50">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {loadError && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          <span>{loadError}</span>
          <button onClick={load} className="shrink-0 px-3 py-1 rounded-lg border border-red-300 bg-white text-xs text-red-700 hover:bg-red-100">
            Retry
          </button>
        </div>
      )}
      {actionError && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="shrink-0 text-xs text-red-700 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {!(loadError && jobs.length === 0 && !loading) && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Job</th>
                <th className="px-4 py-3 font-medium">Schedule</th>
                <th className="px-4 py-3 font-medium">Last run</th>
                <th className="px-4 py-3 font-medium">Enabled</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2 align-text-bottom" />
                    Loading background jobs…
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                    No background jobs are registered yet. Jobs appear here once the scheduler registers them at startup.
                  </td>
                </tr>
              ) : jobs.map(job => {
                const plainSchedule = describeCron(job.schedule);
                return (
                  <tr key={job.job_id} className={!job.enabled ? 'opacity-60' : ''}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{job.name}</div>
                      <div className="text-xs text-gray-400 font-mono">{job.job_id}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {plainSchedule ? (
                        <>
                          <div>{plainSchedule}</div>
                          <div className="font-mono text-gray-400 mt-0.5">{job.schedule}</div>
                        </>
                      ) : (
                        <span className="font-mono">{job.schedule}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {job.last_run ? (
                        <span className="inline-flex items-center gap-1.5">
                          {job.last_run.status === 'success'
                            ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                            : job.last_run.status === 'failed'
                              ? <XCircle className="h-4 w-4 text-red-500" />
                              : <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                          <span className="text-gray-600">
                            {job.last_run.started_at ? new Date(job.last_run.started_at).toLocaleString() : ''}
                          </span>
                        </span>
                      ) : <span className="text-gray-400">never</span>}
                      {job.last_run?.error && (
                        <div className="text-xs text-red-500 mt-0.5 max-w-md truncate" title={job.last_run.error}>
                          Last run failed: {job.last_run.error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggle(job)} disabled={busyJob === job.job_id}
                        title={job.enabled ? 'Turn this job off' : 'Turn this job on'}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border
                          ${job.enabled ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
                        <Power className="h-3 w-3" /> {job.enabled ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => runNow(job)} disabled={busyJob === job.job_id || !job.enabled}
                        title={job.enabled ? 'Run now' : 'Turn the job on to run it'}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs hover:bg-gray-50 disabled:opacity-40 mr-2">
                        {busyJob === job.job_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                        Run
                      </button>
                      <button onClick={() => showHistory(job.job_id)} title="Recent runs"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs hover:bg-gray-50">
                        <History className="h-3 w-3" /> History
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {historyFor && (
        <div className="mt-6 bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-medium text-gray-900">Recent runs: {historyJob?.name ?? historyFor}</h2>
            <button onClick={() => setHistoryFor(null)} className="text-sm text-gray-400 hover:text-gray-600">Close</button>
          </div>
          {historyError && (
            <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">
              {historyError}
            </div>
          )}
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {historyLoading ? (
              <p className="text-sm text-gray-400">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2 align-text-bottom" />Loading runs…
              </p>
            ) : runs.length === 0 ? (
              !historyError && <p className="text-sm text-gray-400">No recorded runs yet. Use Run to start one now.</p>
            ) : runs.map(run => {
              const resultText = describeResult(run.result);
              return (
                <div key={run.id} className="flex items-start gap-2 text-sm border-b pb-2">
                  {run.status === 'success'
                    ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                    : run.status === 'failed'
                      ? <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
                      : <Loader2 className="h-4 w-4 animate-spin text-gray-400 mt-0.5" />}
                  <div className="min-w-0">
                    <div className="text-gray-700">
                      {run.started_at ? new Date(run.started_at).toLocaleString() : ''}
                      {run.finished_at && run.started_at && (
                        <span className="text-gray-400">
                          {' '}({Math.round((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000)}s)
                        </span>
                      )}
                    </div>
                    {run.error && <div className="text-xs text-red-500" title={run.error}>Failed: {run.error}</div>}
                    {resultText && (
                      <div className="text-xs text-gray-500 break-words max-h-20 overflow-y-auto">
                        {resultText}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
